import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS } from "@/lib/api";

const links = [
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Work", href: "#work" },
  { label: "Custom", href: "#quote" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/60 backdrop-blur-2xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <a href="#top" data-testid="logo-link" className="flex flex-col leading-none">
          <span className="font-serif text-2xl tracking-tight text-foreground">Platinum</span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-primary -mt-0.5">Auto &amp; Home</span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="nav-link text-sm tracking-wide text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-5">
          <a href={BUSINESS.phoneHref} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="header-phone">
            <Phone className="w-3.5 h-3.5" /> {BUSINESS.phone}
          </a>
          <a
            href="#book"
            data-testid="header-book-btn"
            className="px-6 py-2.5 bg-primary text-primary-foreground text-sm tracking-wide hover:bg-primary/90 transition-colors clip-frame"
          >
            Book Now
          </a>
        </div>

        <button
          className="md:hidden text-foreground"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          className="md:hidden bg-black/90 backdrop-blur-2xl border-t border-white/10 overflow-hidden"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-lg text-muted-foreground" data-testid={`mobile-nav-${l.label.toLowerCase()}`}>
                {l.label}
              </a>
            ))}
            <a href="#book" onClick={() => setOpen(false)} className="mt-2 px-6 py-3 bg-primary text-primary-foreground text-center text-sm tracking-wide clip-frame" data-testid="mobile-book-btn">
              Book Now
            </a>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
